'use client'

import React, { useEffect, useState, useRef } from "react";
import MainMenu from "./MainMenu";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

const Header = () => {
  const navigate = useNavigate();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState("en");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const langRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation();
  const languages = [
    { code: "en", label: "English" },
    { code: "ar", label: "العربية" },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token_customer_silverjarz');
    const userData = localStorage.getItem('customer_silverjarz');

    if (token && userData) {
      try {
        setUser(JSON.parse(userData) as User);
        setIsLoggedIn(true);
      } catch (error) {
        localStorage.removeItem('token_customer_silverjarz');
        localStorage.removeItem('customer_silverjarz');
      }
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLanguageChange = (code: string) => {
    setSelectedLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem("lang", code);
    document.documentElement.dir = code === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = code;
    setIsLangOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token_customer_silverjarz');
    localStorage.removeItem('customer_silverjarz');
    setIsLoggedIn(false);
    setUser(null);
    setIsProfileOpen(false);
    navigate('/');
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setIsLangOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setIsProfileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const currentLang = languages.find((l) => l.code === selectedLang);

  const PROFILE_LINKS = [
    {
      to: "/my-dashboard",
      label: t("dashboard"),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      ),
    },

  ];

  return (
    <>
      <style>{`
        :root {
          --gold-primary: #d3be76;
          --gold-hover: #e5d18d;
          --dark-bg: rgba(14, 30, 38, 0.75);
          --dark-border: rgba(255, 255, 255, 0.12);
        }

        .glass-header-shell {
          position: fixed;
          top: 16px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100% - 48px);
          max-width: 1320px;
          z-index: 1000;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .glass-header-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 24px;
          background: var(--dark-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--dark-border);
          border-radius: 50px;
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
        }

        .glass-header-shell.scrolled {
          top: 8px;
          width: calc(100% - 24px);
        }

        .brand-logo img {
          height: 38px;
          width: auto;
          display: block;
        }

        .main-nav-wrapper .nav-list {
          display: flex;
          align-items: center;
          gap: 6px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-link {
          position: relative;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.85);
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 500;
          padding: 8px 16px;
          border-radius: 20px;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .nav-link:hover, .nav-link.active {
          color: #fff;
          background: rgba(255, 255, 255, 0.08);
        }

        .nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 14px;
          height: 2px;
          background: var(--gold-primary);
          border-radius: 2px;
        }

        .nav-item-dropdown { position: relative; }

        .dropdown-menu {
          position: absolute;
          top: calc(100% + 12px);
          left: 0;
          width: 180px;
          background: rgba(20, 38, 48, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(211, 190, 118, 0.25);
          border-radius: 16px;
          padding: 8px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.4);
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          max-height: 70vh;
          overflow-y: auto;
        }

        .dropdown-menu.grid-2col {
          width: 420px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2px;
          left: 50%;
          transform: translate(-50%, 10px);
        }

        .dropdown-menu.grid-2col.show {
          transform: translate(-50%, 0);
        }

        .dropdown-menu::before {
          content: '';
          position: absolute;
          top: -12px;
          left: 0;
          width: 100%;
          height: 12px;
        }

        .dropdown-menu.show {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: 10px;
          text-decoration: none;
          color: #fff;
          transition: background 0.2s;
        }

        .nav-item-dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item:hover {
          background: rgba(211, 190, 118, 0.12);
        }

        .item-title {
          color: #fff;
          font-size: 13px;
          font-weight: 600;
        }

        .item-desc {
          color: rgba(255, 255, 255, 0.5);
          font-size: 11px;
          margin-top: 2px;
        }

        .chevron-icon {
          width: 12px;
          height: 12px;
          transition: transform 0.2s;
        }

        .nav-item-dropdown:hover .chevron-icon {
          transform: rotate(180deg);
        }

        .badge-promo {
          background: linear-gradient(135deg, #e65c00, #F9D423);
          color: #fff;
          font-size: 9px;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 10px;
          text-transform: uppercase;
        }

        .right-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn-pill-lang {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--dark-border);
          color: #fff;
          font-size: 13px;
          padding: 7px 14px;
          border-radius: 30px;
          cursor: pointer;
          transition: border-color 0.2s;
        }

        .btn-pill-lang:hover {
          border-color: var(--gold-primary);
        }

        .btn-auth-gold {
          background: linear-gradient(135deg, var(--gold-primary), #b09a50);
          color: #0e1e26;
          font-weight: 600;
          font-size: 13px;
          padding: 8px 20px;
          border-radius: 30px;
          text-decoration: none;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .btn-auth-gold:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 15px rgba(211, 190, 118, 0.3);
          color: #0e1e26;
        }

        /* PROFILE DROPDOWN — identity header + icons */
        .profile-dropdown { width: 250px; padding: 10px; }
        .profile-dropdown-head {
          display: flex; align-items: center; gap: 12px;
          padding: 6px 8px 14px;
        }
        .profile-avatar-lg {
          width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
          background: linear-gradient(135deg, var(--gold-primary), #b09a50);
          color: #0e1e26; font-weight: 700; font-size: 16px;
          display: flex; align-items: center; justify-content: center;
        }
        .profile-name {
          color: #fff; font-size: 13.5px; font-weight: 600; line-height: 1.3;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .profile-email {
          color: rgba(255,255,255,.5); font-size: 11.5px; margin-top: 2px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .dropdown-divider { height: 1px; background: rgba(255,255,255,0.1); margin: 4px 6px 8px; }
        .dropdown-item-icon {
          width: 26px; height: 26px; border-radius: 8px; flex-shrink: 0;
          background: rgba(211,190,118,0.12); color: var(--gold-primary);
          display: flex; align-items: center; justify-content: center;
        }
        .dropdown-item-label { font-size: 13px; font-weight: 500; color: #fff; }
        .dropdown-item-danger { color: #ef4444; cursor: pointer; }
        .dropdown-item-danger .dropdown-item-icon { background: rgba(239,68,68,0.12); color: #ef4444; }
        .dropdown-item-danger .dropdown-item-label { color: #ef4444; }

        @media (max-width: 991px) {
          .glass-header-shell { width: calc(100% - 20px); top: 10px; }
          .main-nav-wrapper { display: none; }
        }
      `}</style>

      <header className={`glass-header-shell ${isScrolled ? 'scrolled' : ''}`}>
        <div className="glass-header-bar">

          {/* Logo */}
          <Link to="/" className="brand-logo">
            <img src="/img/logo_white.png" alt="Silver Jarz" />
          </Link>

          {/* Centered Modern Navigation */}
          <MainMenu />

          {/* Right Controls */}
          <div className="right-actions">

            {/* Language Pill */}
            <div className="lang-selector" ref={langRef}>
              <button
                className="btn-pill-lang"
                onClick={() => setIsLangOpen(!isLangOpen)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--gold-primary)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                <span>{currentLang?.label}</span>
              </button>

              {isLangOpen && (
                <div className="dropdown-menu show" style={{ left: 'auto', right: 0 }}>
                  {languages.map((l) => (
                    <div
                      key={l.code}
                      className="dropdown-item"
                      onClick={() => handleLanguageChange(l.code)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="item-title">{l.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Auth Button or User Profile */}
            {isLoggedIn && user ? (
              <div className="profile-selector" ref={profileRef}>
                <button
                  className="btn-pill-lang"
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  style={{ border: '1px solid var(--gold-primary)' }}
                >
                  <span style={{ color: 'var(--gold-primary)', fontWeight: 'bold' }}>
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                  <span>{user.name.split(' ')[0]}</span>
                </button>

                {isProfileOpen && (
                  <div className="dropdown-menu profile-dropdown show" style={{ left: 'auto', right: 0 }}>
                    <div className="profile-dropdown-head">
                      <div className="profile-avatar-lg">{user.name.charAt(0).toUpperCase()}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className="profile-name">{user.name}</div>
                        <div className="profile-email">{user.email}</div>
                      </div>
                    </div>

                    <div className="dropdown-divider" />

                    {PROFILE_LINKS.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="dropdown-item"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <span className="dropdown-item-icon">{link.icon}</span>
                        <span className="dropdown-item-label">{link.label}</span>
                      </Link>
                    ))}

                    <div className="dropdown-divider" />

                    <div className="dropdown-item dropdown-item-danger" onClick={handleLogout}>
                      <span className="dropdown-item-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </span>
                      <span className="dropdown-item-label">{t("signOut")}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn-auth-gold">
                {t("signIn")}
              </Link>
            )}

          </div>
        </div>
      </header>
    </>
  );
};

export default Header;